<template>
  <v-container class="ari-pulse-policy-page container-wrapper">
    <v-row>
      <v-col class="ari-pulse-policy-page__header px-xl-0">
        <h1>ARI Pulse Privacy Policy</h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="ari-pulse-policy-page__content px-xl-0">
        <p>
          This Privacy Policy applies specifically to ARI Pulse, available at
          <a href="https://www.ari.company/ari-pulse" target="_blank" rel="noopener noreferrer">https://www.ari.company/ari-pulse</a>.
          It explains what data is processed when you use ARI Pulse, how it is used, and your rights.
        </p>

        <h4>1. Controller</h4>
        <p>
          ARI Pulse is provided by <strong>Lidiia Dankevych, trading as ARI.company</strong>
          ("ARI.company", "we", "us", "our").
        </p>

        <h4>2. What ARI Pulse Does</h4>
        <p>
          ARI Pulse is a read-only analytics interface for Google Search Console (GSC). It allows users to connect a GSC account,
          select a property, choose a reporting mode (ISO week or rolling window), optionally compare with a previous period,
          and review/export structured report output.
        </p>

        <h4>3. Data Processed Through ARI Pulse</h4>
        <p>When you use ARI Pulse, we may process the following categories of data:</p>
        <ul>
          <li>Session metadata required to maintain your temporary ARI Pulse connection session.</li>
          <li>Google OAuth response data required to obtain a temporary access token for read-only GSC queries.</li>
          <li>GSC performance metrics returned by Google APIs (for example clicks, impressions, CTR, and average position).</li>
          <li>Technical diagnostics and security logs required to keep the service stable, secure, and abuse-resistant.</li>
        </ul>

        <h4>4. Read-Only Access and Scope</h4>
        <p>
          ARI Pulse requests the scope <code>https://www.googleapis.com/auth/webmasters.readonly</code>.
          This is a read-only scope and does not allow ARI Pulse to publish, edit, or delete your Search Console data.
        </p>

        <h4>5. Session Model and Retention</h4>
        <p>
          ARI Pulse is designed as a privacy-first session workflow. Session tokens are temporary and expire automatically.
          If your session expires, refreshes, or is terminated, you must reconnect.
        </p>
        <p>
          We do not operate ARI Pulse as a persistent long-term customer data warehouse by default. Report output is generated on demand
          for your active session and delivered directly to you.
        </p>

        <h4>6. Exported Reports</h4>
        <p>
          ARI Pulse may generate downloadable report files (such as XLSX). You are responsible for how you store, transfer, and protect
          exported files once they are downloaded to your own environment.
        </p>

        <h4>7. Legal Basis</h4>
        <p>
          Depending on context and jurisdiction, processing is carried out on one or more of the following bases:
        </p>
        <ul>
          <li>Your consent (where required).</li>
          <li>Performance of a requested service action initiated by you.</li>
          <li>Legitimate interests in operating, securing, and improving ARI Pulse.</li>
          <li>Compliance with legal obligations, where applicable.</li>
        </ul>

        <h4>8. Third-Party Services</h4>
        <p>
          ARI Pulse depends on third-party infrastructure and API providers, including Google services used for authentication
          and Search Console data retrieval. Their processing of your data is governed by their own policies and terms.
        </p>

        <h4>9. Security Measures</h4>
        <p>
          We apply reasonable technical and organisational safeguards designed to protect ARI Pulse sessions and service operations.
          No internet service can be guaranteed as fully risk-free, but we aim to follow practical security standards in production.
        </p>

        <h4>10. Your Rights</h4>
        <p>
          Subject to applicable law, you may have rights to access, correct, restrict, object to, or erase personal data,
          and to withdraw consent where consent is the basis of processing.
        </p>
        <p>
          To submit a request, contact us at
          <a href="mailto:privacy@ari.company">privacy@ari.company</a> with the subject line
          <strong>ARI Pulse Privacy Request</strong>.
        </p>

        <h4>11. Updates to This Policy</h4>
        <p>
          We may update this ARI Pulse Privacy Policy from time to time to reflect legal, technical, or operational changes.
          The latest published version at this URL applies.
        </p>

        <h4>12. Contact</h4>
        <p>
          For ARI Pulse privacy enquiries, email
          <a href="mailto:privacy@ari.company">privacy@ari.company</a>.
        </p>
        <p><i>Effective: April 7, 2026</i></p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'AriPulsePrivacyPage'
}
</script>

<style lang="scss" scoped>
.ari-pulse-policy-page {
  flex: 1 0 auto;

  &__header {
    padding-top: 60px;
  }

  &__content {
    padding-bottom: 60px;
    word-wrap: break-word;

    h4 {
      margin: 18px 0 10px;
    }

    p {
      font-size: 16px;
      margin-bottom: 12px;
    }

    ul {
      margin-bottom: 14px;
      color: $dark-text;
    }

    code {
      font-size: 0.95em;
      background: rgba(35, 70, 118, 0.08);
      padding: 2px 6px;
      border-radius: 6px;
    }
  }
}
</style>
